"use strict";
/**
 * This module contains classes for a conversion parser.
 * @author Louis-Dominique Dubeau
 * @license MPL 2.0
 * @copyright Mangalam Research Center for Buddhist Languages
 */
Object.defineProperty(exports, "__esModule", { value: true });
const saxes_1 = require("saxes");
const ename_1 = require("../ename");
const name_resolver_1 = require("../name_resolver");
const tools_1 = require("../tools");
const util_1 = require("./simplifier/util");
class Node {
    get parent() {
        return this._parent;
    }
    set parent(value) {
        this.setParent(value);
    }
    setParent(value) {
        this._parent = value;
    }
    remove() {
        const parent = this.parent;
        if (parent !== undefined) {
            parent.removeChild(this);
        }
    }
    replaceWith(replacement) {
        const parent = this.parent;
        if (parent === undefined) {
            throw new Error("no parent");
        }
        parent.replaceChildWith(this, replacement);
    }
}
exports.Node = Node;
const emptyNS = Object.create(null);
/**
 * An Element produced by [[BasicParser]].
 *
 * This constructor will insert the created object into the parent automatically
 * if the parent is provided.
 */
class Element extends Node {
    /**
     * @param node The value of the ``node`` created by the SAX parser.
     *
     * @param children The children of this element. **These children must not yet
     * be children of any element.**
     */
    constructor(prefix, local, uri, ns, attributes, children = []) {
        super();
        this.children = children;
        this.kind = "element";
        this.prefix = prefix;
        this.local = local;
        this.uri = uri;
        // Namespace declarations are immutable.
        this.ns = ns;
        this.attributes = attributes;
        for (const child of children) {
            child.parent = this;
        }
    }
    static fromSax(node, children) {
        return new Element(node.prefix, node.local, node.uri, node.ns, node.attributes, children);
    }
    static makeElement(name) {
        return new Element("", name, "", 
        // We always pass the same object as ns. So we save an unnecessary object
        // creation.
        emptyNS, Object.create(null));
    }
    setParent(value) {
        //
        // The cost of looking for cycles is noticeable. So we should use this
        // only when debugging new code.
        //
        // let scan = value;
        // while (scan !== undefined) {
        //   if (scan === this) {
        //     throw new Error("creating reference loop!");
        //   }
        //   scan = scan.parent;
        // }
        this._path = undefined; // This becomes void.
        // We inline super.setParent here:
        this._parent = value;
    }
    resolve(name) {
        if (name === "xml") {
            return name_resolver_1.XML1_NAMESPACE;
        }
        if (name === "xmlns") {
            return name_resolver_1.XMLNS_NAMESPACE;
        }
        return this._resolve(name);
    }
    _resolve(name) {
        const ret = this.ns[name];
        if (ret !== undefined) {
            return ret;
        }
        return (this.parent === undefined) ? undefined : this.parent._resolve(name);
    }
    get text() {
        return this.children.map((x) => x.text).join("");
    }
    /**
     * A path describing the location of the element in the XML. Note that this is
     * meant to be used **only** after the simplification is complete. The value
     * is computed once and for all as soon as it is accessed.
     */
    get path() {
        if (this._path === undefined) {
            this._path = this.makePath();
        }
        return this._path;
    }
    makePath() {
        let ret = `${(this.parent !== undefined) ? this.parent.path : ""}/${this.local}`;
        const name = this.getAttribute("name");
        if (name !== undefined) {
            // tslint:disable-next-line:no-string-literal
            ret += `[@name='${name}']`;
        }
        // Name classes are only valid on elements and attributes. So don't go
        // searching for it on other elements.
        else if (this.local === "element" || this.local === "attribute") {
            // By the time path is used, the name class is the first child.
            const first = this.children[0];
            if (isElement(first) && first.local === "name") {
                ret += `[@name='${first.text}']`;
            }
        }
        return ret;
    }
    removeChild(child) {
        // We purposely don't call removeChildAt, so as to save a call.
        //
        // We don't check whether there's an element at [0]. If not, a hard fail is
        // appropriate. It shouldn't happen.
        this.children.splice(this.indexOfChild(child), 1)[0].parent = undefined;
    }
    removeChildAt(i) {
        // We don't check whether there's an element at [0]. If not, a hard fail is
        // appropriate. It shouldn't happen.
        this.children.splice(i, 1)[0].parent = undefined;
    }
    replaceChildWith(child, replacement) {
        this.replaceChildAt(this.indexOfChild(child), replacement);
    }
    replaceChildAt(i, replacement) {
        const child = this.children[i];
        // In practice this is not a great optimization.
        //
        // if (child === replacement) {
        //   return;
        // }
        if (replacement.parent !== undefined) {
            replacement.parent.removeChild(replacement);
        }
        this.children[i] = replacement;
        child.parent = undefined;
        replacement.parent = this;
    }
    appendChild(child) {
        // It is faster to use custom code than to rely on insertAt: splice
        // operations are costly.
        if (child.parent !== undefined) {
            child.parent.removeChild(child);
        }
        child.parent = this;
        this.children.push(child);
    }
    appendChildren(children) {
        // It is faster to use custom code than to rely on insertAt: splice
        // operations are costly.
        for (const el of children) {
            if (el.parent !== undefined) {
                el.parent.removeChild(el);
            }
            el.parent = this;
        }
        this.children.push(...children);
    }
    prependChild(child) {
        // It is faster to do this than to rely on insertAt: splice operations
        // are costly.
        if (child.parent !== undefined) {
            child.parent.removeChild(child);
        }
        child.parent = this;
        this.children.unshift(child);
    }
    insertAt(index, toInsert) {
        for (const el of toInsert) {
            if (el.parent !== undefined) {
                el.parent.removeChild(el);
            }
            el.parent = this;
        }
        this.children.splice(index, 0, ...toInsert);
    }
    empty() {
        const children = this.children.splice(0, this.children.length);
        for (const child of children) {
            child.parent = undefined;
        }
    }
    /**
     * Gets all the children from another element and append them to this
     * element. This is a faster operation than done through other means.
     *
     * @param src The element form which to get the children.
     */
    grabChildren(src) {
        const children = src.children.splice(0, src.children.length);
        this.children.push(...children);
        for (const child of children) {
            child.parent = this;
        }
    }
    replaceContent(children) {
        const prev = this.children.splice(0, this.children.length, ...children);
        for (const child of prev) {
            child.parent = undefined;
        }
        for (const child of children) {
            child.parent = this;
        }
    }
    indexOfChild(child) {
        const parent = child.parent;
        if (parent !== this) {
            throw new Error("the child is not a child of this");
        }
        const index = parent.children.indexOf(child);
        if (index === -1) {
            throw new Error("child not among children");
        }
        return index;
    }
    /**
     * Set an attribute on an element.
     *
     * @param name The attribute name.
     *
     * @param value The new value of the attribute.
     */
    setAttribute(name, value) {
        if (name.includes(":")) {
            throw new Error("we don't support namespaces on this function");
        }
        this.attributes[name] = {
            name: name,
            prefix: "",
            local: name,
            uri: "",
            value: value,
        };
    }
    setXMLNS(value) {
        this.attributes.xmlns = {
            name: "xmlns",
            prefix: "",
            uri: name_resolver_1.XMLNS_NAMESPACE,
            value,
            local: "xmlns",
        };
    }
    removeAttribute(name) {
        delete this.attributes[name];
    }
    getAttribute(name) {
        const attr = this.attributes[name];
        return (attr !== undefined) ? attr.value : undefined;
    }
    getRawAttributes() {
        return this.attributes;
    }
    mustGetAttribute(name) {
        const attr = this.getAttribute(name);
        if (attr === undefined) {
            throw new Error(`no attribute named ${name}`);
        }
        return attr;
    }
    clone() {
        // The strategy of pre-filling the new object and then updating the keys
        // appears to be faster than inserting new keys one by one.
        const attributes = Object.assign(Object.create(null), this.attributes);
        for (const key of Object.keys(attributes)) {
            // We do not use Object.create(null) here because there's no advantage
            // to it.
            attributes[key] = Object.assign({}, attributes[key]);
        }
        return new Element(this.prefix, this.local, this.uri, this.ns, attributes, this.children.map((child) => child.clone()));
    }
}
exports.Element = Element;
class Text extends Node {
    /**
     * @param text The textual value.
     */
    constructor(text) {
        super();
        this.text = text;
        this.kind = "text";
    }
    clone() {
        return new Text(this.text);
    }
}
exports.Text = Text;
function isElement(node) {
    return node.kind === "element";
}
exports.isElement = isElement;
function isText(node) {
    return node.kind === "text";
}
exports.isText = isText;
class SaxesNameResolver {
    constructor(saxesParser) {
        this.saxesParser = saxesParser;
    }
    resolveName(name, attribute = false) {
        const colon = name.indexOf(":");
        let prefix;
        let local;
        if (colon === -1) {
            if (attribute) { // Attribute in undefined namespace
                return new ename_1.EName("", name);
            }
            // We are searching for the default namespace currently in effect.
            prefix = "";
            local = name;
        }
        else {
            prefix = name.substring(0, colon);
            local = name.substring(colon + 1);
            if (local.includes(":")) {
                throw new Error("invalid name passed to resolveName");
            }
        }
        const uri = this.saxesParser.resolve(prefix);
        if (uri !== undefined) {
            return new ename_1.EName(uri, local);
        }
        return (prefix === "") ? new ename_1.EName("", local) : undefined;
    }
    clone() {
        throw new Error("cannot clone a SaxesNameResolver");
    }
}
class Validator {
    constructor(grammar, parser) {
        /** Whether we ran into an error. */
        this.errors = [];
        this.walker = grammar.newWalker(new SaxesNameResolver(parser));
    }
    fireEvent(name, args) {
        const ret = this.walker.fireEvent(name, args);
        if (ret) {
            this.errors.push(...ret);
        }
    }
    onopentag(node) {
        const { attributes } = node;
        const params = [node.uri, node.local];
        for (const name of Object.keys(attributes)) {
            const { uri, prefix, local, value } = attributes[name];
            // xmlns="..." or xmlns:q="..."
            if (!(name === "xmlns" || prefix === "xmlns")) {
                params.push(uri, local, value);
            }
        }
        this.fireEvent("startTagAndAttributes", params);
    }
    onclosetag(node) {
        this.fireEvent("endTag", [node.uri, node.local]);
    }
    ontext(text) {
        this.fireEvent("text", [text]);
    }
}
exports.Validator = Validator;
// A validator that does not validate.
class NullValidator {
    // tslint:disable-next-line:no-empty
    onopentag() { }
    // tslint:disable-next-line:no-empty
    onclosetag() { }
    // tslint:disable-next-line:no-empty
    ontext() { }
}
/**
 * A simple parser used for loading a XML document into memory.  Parsers of this
 * class use [[Node]] objects to represent the tree of nodes.
 */
class BasicParser {
    constructor(saxesParser, validator = new NullValidator()) {
        this.saxesParser = saxesParser;
        this.validator = validator;
        this.drop = 0;
        saxesParser.onopentag = this.onopentag.bind(this);
        saxesParser.onclosetag = this.onclosetag.bind(this);
        saxesParser.ontext = this.ontext.bind(this);
        this.stack = [{
                // We cheat. The node field of the top level stack item won't ever be
                // accessed.
                node: undefined,
                children: [],
            }];
    }
    /**
     * The root of the parsed XML.
     */
    get root() {
        return this.stack[0].children.filter(isElement)[0];
    }
    onopentag(node) {
        // We have to validate the node even if we are not going to record it,
        // because RelaxNG does not allow foreign nodes everywhere.
        this.validator.onopentag(node);
        // We can skip creating Element objects for foreign nodes and their
        // children.
        if (node.uri !== util_1.RELAXNG_URI || this.drop !== 0) {
            this.drop++;
            return;
        }
        this.stack.push({
            node,
            children: [],
        });
    }
    onclosetag(node) {
        // We have to validate the node even if we are not going to record it,
        // because RelaxNG does not allow foreign nodes everywhere.
        this.validator.onclosetag(node);
        if (this.drop !== 0) {
            this.drop--;
            return;
        }
        // tslint:disable-next-line:no-non-null-assertion
        const { node: topNode, children } = this.stack.pop();
        this.stack[this.stack.length - 1].children
            .push(Element.fromSax(topNode, children));
    }
    ontext(text) {
        this.validator.ontext(text);
        if (this.drop !== 0) {
            return;
        }
        this.stack[this.stack.length - 1].children.push(new Text(text));
    }
}
exports.BasicParser = BasicParser;
/**
 * This parser is specifically dedicated to the task of reading simplified Relax
 * NG schemas. In a Relax NG schema, text nodes that consist entirely of white
 * space are expendable, except in the ``param`` and ``value`` elements, where
 * they do potentially carry significant information.
 *
 * This parser strips nodes that consist entirely of white space because this
 * simplifies code that needs to process the resulting tree, but preserve those
 * nodes that are potentially significant.
 *
 * This parser does not allow elements which are not in the Relax NG namespace.
 */
class ConversionParser extends BasicParser {
    onopentag(node) {
        // tslint:disable-next-line: no-http-string
        if (node.uri !== "http://relaxng.org/ns/structure/1.0") {
            throw new Error(`node in unexpected namespace: ${node.uri}`);
        }
        super.onopentag(node);
    }
    ontext(text) {
        // We ignore text appearing before or after the top level element.
        if (this.stack.length <= 1 || this.drop !== 0) {
            return;
        }
        const top = this.stack[this.stack.length - 1];
        const local = top.node.local;
        // The parser does not allow non-RNG nodes, so we don't need to check the
        // namespace.
        const keepWhitespaceNodes = local === "param" || local === "value";
        if (keepWhitespaceNodes || text.trim() !== "") {
            super.ontext(text);
        }
    }
}
function parseSimplifiedSchema(fileName, simplifiedSchema) {
    const convParser = new ConversionParser(new saxes_1.SaxesParser({ xmlns: true,
        position: false,
        fileName }));
    convParser.saxesParser.write(simplifiedSchema).close();
    return convParser.root;
}
exports.parseSimplifiedSchema = parseSimplifiedSchema;
// Exception used to terminate the saxes parser early.
class Found extends Error {
    constructor() {
        super();
        tools_1.fixPrototype(this, Found);
    }
}
class IncludeParser {
    constructor(saxesParser) {
        this.saxesParser = saxesParser;
        saxesParser.onopentag = this.onopentag.bind(this);
    }
    onopentag(node) {
        // tslint:disable-next-line:no-http-string
        if (node.uri === "http://relaxng.org/ns/structure/1.0" &&
            (node.local === "include" || node.local === "externalRef")) {
            throw new Found(); // Stop early.
        }
    }
}
/**
 * Determine whether an RNG file depends on another file either through the use
 * of ``include`` or ``externalRef``.
 *
 * @param rng The RNG file to check.
 *
 * @returns ``true`` if dependent, ``false`` if not.
 */
function dependsOnExternalFile(rng) {
    const parser = new IncludeParser(new saxes_1.SaxesParser({ xmlns: true, position: false }));
    let found = false;
    try {
        parser.saxesParser.write(rng).close();
    }
    catch (ex) {
        if (!(ex instanceof Found)) {
            throw ex;
        }
        found = true;
    }
    return found;
}
exports.dependsOnExternalFile = dependsOnExternalFile;
//  LocalWords:  MPL NG param RNG
//# sourceMappingURL=parser.js.map